import React from 'react'

import Dialog, { DialogBody, DialogHeader } from '../../Dialog'
import { Avatar } from '../../Avatar'
import { runtime } from '@deltachat-desktop/runtime-interface'
import { instanceHidesPublicSuggestions } from '../../../utils/slothfulInstanceConfig'

import type { DialogProps } from '../../../contexts/DialogContext'

// Same row design as the Public Bots list.
import styles from '../PublicBots/styles.module.scss'

// One flat list for now. A 'More channels' separator
// (<div className='group-separator'>More channels</div>) used to sit before
// FediMeteo — it was meant to divide an inline channel list (fetched from
// arcanechat.me) from these external links, but ArcaneChat's channel list
// has no API to fetch from yet. Restore it when channels render inline.
const CHANNEL_SOURCES = [
  {
    name: 'ArcaneChat Channels',
    url: 'https://arcanechat.me/channels',
    urlLabel: 'arcanechat.me/channels',
    description: 'Browse and join community channels',
    emoji: '📣',
    color: 'hsl(266, 54%, 46%)',
  },
  {
    name: 'FediMeteo',
    url: 'https://fedimeteo.com/',
    urlLabel: 'fedimeteo.com',
    description:
      'Weather forecasts over Delta Chat — FediMeteo already offers a ' +
      'channel for many cities, just pick yours',
    emoji: '🌦️',
    color: 'hsl(207, 54%, 46%)',
  },
]

function ChannelSourceItem({
  source,
  onClose,
}: {
  source: (typeof CHANNEL_SOURCES)[number]
  onClose: () => void
}) {
  return (
    <li className='contact-list-item'>
      <button
        type='button'
        className={'contact-list-item-button ' + styles.botListItem}
        onClick={() => {
          // slothfulchat: anonymous analytics — a community channel was used
          ;(window as any).__slothfulTrack?.('community')
          runtime.openLink(source.url)
          onClose()
        }}
      >
        <Avatar displayName={source.emoji} color={source.color} aria-hidden />
        <div className={styles.botInfo}>
          <div className={styles.botHeadline}>
            <span className={styles.botName}>{source.name}</span>
          </div>
          <div className={styles.botAdmin}>{source.urlLabel}</div>
          <div className={styles.botDescription}>{source.description}</div>
        </div>
      </button>
    </li>
  )
}

/**
 * Links to (third-party) websites listing public channels one can join,
 * reachable from the "New Chat" dialog.
 */
export default function PublicChannelsDialog(props: DialogProps) {
  const { onClose } = props

  if (instanceHidesPublicSuggestions()) {
    // Nothing should open this dialog when the operator hid the community
    // suggestions instance-wide, but if something does: show nothing.
    return null
  }

  return (
    <Dialog
      width={400}
      onClose={onClose}
      dataTestid='public-channels-dialog'
      fullscreenOnMobile
    >
      <DialogHeader title='Public Channels' onClose={onClose} />
      <DialogBody>
        <ol className={styles.botList}>
          {CHANNEL_SOURCES.map(source => (
            <ChannelSourceItem
              key={source.url}
              source={source}
              onClose={onClose}
            />
          ))}
        </ol>
        <div className={styles.footerNote}>
          These are external, community-run websites — their channels are not
          checked or endorsed by us.
        </div>
      </DialogBody>
    </Dialog>
  )
}
